import {
  kilometresBetween,
  minutesBetween,
  minutesToPx,
  type Coordinates,
} from './geometry.js'
import type { BoardVisit } from './types.js'

/**
 * Drive time drawn in front of a job.
 *
 * Stored `travelMinutes` wins when the visit has one. Otherwise a straight-line
 * hop at a conservative urban speed, labelled "direct" wherever it is shown —
 * it is not a routing product (decision 27).
 */

/** Kilometres per hour used when we only have a straight line. */
export const URBAN_KMH = 30

/**
 * Where a drawn drive time came from, best first.
 *
 * `google` is traffic-aware and directional, fetched through `routing.travel`
 * from the cache the route planner fills. `stored` is `ScheduledVisit.travelMinutes`
 * — either what the router stamped when a plan was applied, or fifteen years of
 * ServiceM8 measurements. `direct` is a straight line at an urban speed and is
 * labelled as such wherever it is shown, because it is a floor and not an
 * answer.
 *
 * Ranked rather than merged: a stripe that cannot say which of these it is
 * invites a dispatcher to trust a guess as though it were measured.
 */
export type TravelSource = 'google' | 'stored' | 'direct' | 'none'

export interface TravelHop {
  visitId: string
  minutes: number
  source: TravelSource
  /** The gap before this job is shorter than the hop needs. */
  tight: boolean
  /**
   * How many travel minutes sit on top of the previous booking. Zero when the
   * jobs overlap in time (those stack into lanes) or when the gap is long
   * enough — this is only the shortfall on a same-lane hop.
   */
  overlapMinutes: number
}

/** Minutes of drive time that paint over the previous booking. */
export function travelOverlapMinutes(driveMinutes: number, gapMinutes: number): number {
  if (!Number.isFinite(gapMinutes) || gapMinutes < 0 || driveMinutes <= 0) return 0
  return Math.max(0, driveMinutes - gapMinutes)
}

/**
 * How much of the *drawn* stripe sits on the previous booking.
 *
 * The stripe clips at the window start from the left (timeline) / top (day),
 * and that cut is the overlap, so a hop near 4am can lose its shortfall
 * entirely and must not still paint a translucent band on the gap.
 */
export function travelOverlapSize(
  overlapMinutes: number,
  driveMinutes: number,
  stripeSize: number,
  slotPx: number,
): number {
  if (overlapMinutes <= 0 || driveMinutes <= 0 || stripeSize <= 0) return 0
  const clipped = Math.max(0, minutesToPx(driveMinutes, slotPx) - stripeSize)
  return Math.max(0, Math.min(stripeSize, minutesToPx(overlapMinutes, slotPx) - clipped))
}

export function coordsOf(visit: BoardVisit): Coordinates | null {
  return coordsOfAddress(visit.job.site?.address)
}

export function coordsOfAddress(
  address: { latitude?: number | null; longitude?: number | null } | null | undefined,
): Coordinates | null {
  if (address?.latitude == null || address.longitude == null) return null
  return { latitude: address.latitude, longitude: address.longitude }
}

export function isLiveVisit(visit: { status?: string }): boolean {
  return visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW'
}

/**
 * Minutes for a hop. Zero when there is nothing to measure, so a first job
 * of the day with no stored travel draws no stripe.
 */
export function estimateTravelMinutes(
  from: Coordinates | null,
  to: Coordinates | null,
  stored: number | null,
  measured?: number | null,
): { minutes: number; source: TravelSource } {
  // A live traffic-aware figure outranks a stored one, because the stored
  // number may be a five-year-old ServiceM8 measurement of a road that has had
  // a freeway interchange built on it since.
  if (measured != null && measured > 0) return { minutes: measured, source: 'google' }
  if (stored != null && stored > 0) return { minutes: stored, source: 'stored' }
  if (!from || !to) return { minutes: 0, source: 'none' }

  const km = kilometresBetween(from, to)
  return { minutes: Math.max(1, Math.round((km / URBAN_KMH) * 60)), source: 'direct' }
}

export interface TravelStop {
  id: string
  startAt: Date
  endAt: Date
  coords: Coordinates | null
  travelMinutes?: number | null
  live?: boolean
  /**
   * A break with no address is a hole in the run: not a hop and not the
   * place the next hop leaves from. Visits always occupy a place.
   */
  occupiesPlace?: boolean
}

/**
 * Minutes for one hop from a measured source, or null when nobody measured it.
 *
 * Passed in rather than imported so this file stays pure and testable; the
 * board supplies `useMeasuredTravel()`.
 */
export type TravelMeasure = (from: Coordinates, to: Coordinates, departAt: Date) => number | null

/** One hop per live stop that occupies a place, in start order. */
export function hopsForStops(stops: TravelStop[], measure?: TravelMeasure): TravelHop[] {
  const live = [...stops]
    .filter((stop) => stop.live !== false && stop.occupiesPlace !== false)
    .sort((a, b) => a.startAt.getTime() - b.startAt.getTime())

  return live.map((stop, index) => {
    const prev = live[index - 1]
    const from = prev?.coords ?? null
    const to = stop.coords
    const measured = measure && prev && from && to ? measure(from, to, prev.endAt) : null
    const { minutes, source } = estimateTravelMinutes(from, to, stop.travelMinutes ?? null, measured)
    const gap = prev ? minutesBetween(prev.endAt, stop.startAt) : Number.POSITIVE_INFINITY

    return {
      visitId: stop.id,
      minutes,
      source,
      tight: minutes > 0 && Number.isFinite(gap) && gap < minutes,
      overlapMinutes: travelOverlapMinutes(minutes, gap),
    }
  })
}

/** One hop per live visit, in start order, for drawing travel stripes. */
export function hopsFor(visits: BoardVisit[], measure?: TravelMeasure): TravelHop[] {
  return hopsForStops(
    visits.map((visit) => ({
      id: visit.id,
      startAt: visit.startAt,
      endAt: visit.endAt,
      coords: coordsOf(visit),
      travelMinutes: visit.travelMinutes,
      live: isLiveVisit(visit),
    })),
    measure,
  )
}

/** Sum of hop minutes for a van's day, ignoring cancelled work. */
export function totalDriveMinutes(visits: BoardVisit[]): number {
  return hopsFor(visits).reduce((sum, hop) => sum + hop.minutes, 0)
}
